import '@/App.css';
import '@/index.css';
import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { SiteSettingsProvider } from '@/contexts/SiteSettingsContext';
import { Toaster } from '@/components/ui/sonner';
import CountdownPage from '@/pages/CountdownPage';

// Pages
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import AuthCallback from '@/pages/AuthCallback';
import AssessmentPage from '@/pages/AssessmentPage';
import ResultsPage from '@/pages/ResultsPage';
import DashboardPage from '@/pages/DashboardPage';
import CareerDetailPage from '@/pages/CareerDetailPage';
import LeadFormPage from '@/pages/LeadFormPage';
import AdminPage from '@/pages/AdminPage';
import SharedAssessmentPage from '@/pages/SharedAssessmentPage';
import Skill2IncomePage from '@/pages/Skill2IncomePage';
import FAQPage from '@/pages/FAQPage';
import AboutPage from '@/pages/AboutPage';
import FounderPage from '@/pages/FounderPage';
import ContactPage from '@/pages/ContactPage';
import PrivacyPage from '@/pages/PrivacyPage';
import TermsPage from '@/pages/TermsPage';
import LegalPage from '@/pages/LegalPage';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/auth" />;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Handle Google OAuth callback synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
      <Route path="/auth" element={!user ? <AuthPage /> : <Navigate to="/dashboard" />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/faqs" element={<FAQPage />} />
      <Route path="/assessment" element={<ProtectedRoute><AssessmentPage /></ProtectedRoute>} />
      <Route path="/results/:assessmentId" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/career/:pathId" element={<ProtectedRoute><CareerDetailPage /></ProtectedRoute>} />
      <Route path="/share/:assessmentId" element={<SharedAssessmentPage />} />
      <Route path="/form" element={<LeadFormPage />} />
      <Route path="/skill2income" element={<Skill2IncomePage />} />
      <Route path="/KKMxAipdma" element={<AdminPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/founder" element={<FounderPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/legal" element={<LegalPage />} />
    </Routes>
  );
};

const LAUNCH_TIME = new Date('2026-04-07T06:28:00Z'); // April 7, 2026 11:58 AM IST

function App() {
  const [showCountdown, setShowCountdown] = useState(() => {
    const now = new Date();
    return now < LAUNCH_TIME;
  });

  useEffect(() => {
    const blockContext = (e) => e.preventDefault();
    const blockKeys = (e) => {
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && ['I','J','C'].includes(e.key)) || (e.ctrlKey && (e.key === 'u' || e.key === 'U'))) {
        e.preventDefault();
      }
    };
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.remove();

    document.addEventListener('contextmenu', blockContext);
    document.addEventListener('keydown', blockKeys);
    return () => {
      document.removeEventListener('contextmenu', blockContext);
      document.removeEventListener('keydown', blockKeys);
    };
  }, []);

  // Countdown temporarily disabled for testing — re-enable before April 7 launch
  // if (showCountdown) {
  //   if (window.location.pathname === '/KKMxAipdma') {
  //   } else {
  //     return <CountdownPage onLaunch={() => setShowCountdown(false)} />;
  //   }
  // }

  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <SiteSettingsProvider>
              <AppRoutes />
              <Toaster position="top-right" />
            </SiteSettingsProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;